import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  AuthResponse, 
  SiteAnalysisResult,
  Campaign,
  User, 
  UserSession,
  RegionsData
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private initDataProvider: (() => string | null) | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8004/api',
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Очищаем старый токен из localStorage (больше не используем)
    localStorage.removeItem('session_token');

    // Request interceptor - ВСЕГДА добавляем init_data к каждому запросу
    this.api.interceptors.request.use(
      (config) => {
        const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true' || import.meta.env.DEV;
        
        // Всегда добавляем init_data через заголовок
        if (this.initDataProvider) {
          const initData = this.initDataProvider();
          if (initData && initData.trim()) {
            // Добавляем init_data в заголовок для ВСЕХ запросов
            config.headers['X-Telegram-Init-Data'] = initData;
            
            if (isDebugMode) {
              console.log('📤 API Request:', config.method?.toUpperCase(), config.url);
            }
          } else if (isDebugMode) {
            // В debug mode без init_data - это нормально
            console.log('📤 API Request (debug mode):', config.method?.toUpperCase(), config.url);
          } else {
            console.warn('⚠️ API Request without init_data:', config.method?.toUpperCase(), config.url);
          }
        } else if (isDebugMode) {
          console.log('📤 API Request (debug mode):', config.method?.toUpperCase(), config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - при 401 закрываем приложение (init_data устарел)
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          console.error('❌ 401 Unauthorized - init_data is invalid or expired');
          
          // Пробуем закрыть Telegram mini app
          const tgWebApp = (window as any).Telegram?.WebApp;
          if (tgWebApp?.close) {
            console.log('🔄 Closing Telegram mini app...');
            tgWebApp.close();
          } else {
            // Fallback - перезагрузка страницы (для debug mode)
            console.log('🔄 Reloading page...');
            window.location.reload();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set a provider function to get fresh init_data when needed
   */
  setInitDataProvider(provider: () => string | null): void {
    this.initDataProvider = provider;
  }

  // Auth endpoints
  async authenticateWithTelegram(initData: string): Promise<AuthResponse> {
    const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true';
    
    if (isDebugMode) {
      console.log('🌐 Sending authentication request');
    }
    
    // init_data уже добавляется через interceptor в заголовок X-Telegram-Init-Data
    // Но для /auth/telegram также передаём в body для совместимости
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post(
      '/auth/telegram',
      { init_data: initData }
    );
    
    if (isDebugMode) {
      console.log('📥 Authentication response:', response.data);
    }
    
    if (response.data.success && response.data.data) {
      if (isDebugMode) {
        console.log('✅ Authentication successful');
      }
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Authentication failed');
  }

  async getUserProfile(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/user/profile');
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Failed to get user profile');
  }

  async logout(): Promise<void> {
    // С init_data авторизацией logout просто закрывает приложение
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp?.close) {
      tgWebApp.close();
    }
  }

  async refreshSession(): Promise<UserSession> {
    // С init_data авторизацией refresh не нужен - init_data валиден пока открыто приложение
    const response: AxiosResponse<ApiResponse<{ session: UserSession }>> = await this.api.post('/auth/refresh');
    
    if (response.data.success && response.data.data) {
      return response.data.data.session;
    }
    
    throw new Error(response.data.error || 'Failed to refresh session');
  }

  async getSessionInfo(): Promise<UserSession> {
    const response: AxiosResponse<ApiResponse<{ session: UserSession }>> = await this.api.get('/auth/session');
    
    if (response.data.success && response.data.data) {
      return response.data.data.session;
    }
    
    throw new Error(response.data.error || 'Failed to get session info');
  }

  // Site analysis endpoints
  async analyzeSite(url: string): Promise<SiteAnalysisResult> {
    const response: AxiosResponse<ApiResponse<SiteAnalysisResult>> = await this.api.post(
      '/site/analyze',
      { url }
    );
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Site analysis failed');
  }

  async getSiteAnalysisHealth(): Promise<{ healthy: boolean; service: string }> {
    const response: AxiosResponse<ApiResponse<{ healthy: boolean; service: string }>> = await this.api.get('/site/health');
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Failed to check site analysis health');
  }

  // Onboarding endpoints
  async updateOnboardingProgress(step: number): Promise<{ 
    onboarding_step: number; 
    onboarding_completed: boolean; 
  }> {
    const response: AxiosResponse<ApiResponse<{ 
      onboarding_step: number; 
      onboarding_completed: boolean; 
    }>> = await this.api.post('/onboarding/progress', { step });
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Failed to update onboarding progress');
  }

  async resetOnboarding(): Promise<{ 
    onboarding_step: number; 
    onboarding_completed: boolean; 
  }> {
    const response: AxiosResponse<ApiResponse<{ 
      onboarding_step: number; 
      onboarding_completed: boolean; 
    }>> = await this.api.post('/onboarding/reset');
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error(response.data.error || 'Failed to reset onboarding');
  }

  // Get saved onboarding data for returning users
  async getOnboardingData(telegramUserId: number): Promise<{
    has_data: boolean;
    campaign_id?: number;
    url?: string;
    analysis?: SiteAnalysisResult;
    campaign_form_data?: {
      goal: 'sales' | 'leads' | 'awareness' | null;
      budget: string;
      geo: string | null;
    };
    creatives?: {
      keywords?: string[];
      negatives?: string[];
      ads?: any[];
      graphic?: any;
      images?: Array<{
        id: number;
        url: string;
        size: string;
        concept_index: number;
        concept_name: string;
        prompt: string;
      }>;
    };
  }> {
    try {
      console.log('📡 API: Loading saved onboarding data', { telegramUserId });
      
      const response: AxiosResponse<ApiResponse<{
        has_data: boolean;
        campaign_id?: number;
        url?: string;
        analysis?: SiteAnalysisResult;
        campaign_form_data?: {
          goal: 'sales' | 'leads' | 'awareness' | null;
          budget: string;
          geo: string | null;
        };
        creatives?: {
          keywords?: string[];
          negatives?: string[];
          ads?: any[];
          graphic?: any;
          images?: Array<{
            id: number;
            url: string;
            size: string;
            concept_index: number;
            concept_name: string;
            prompt: string;
          }>;
        };
      }>> = await this.api.get('/onboarding/data', {
        params: { telegram_user_id: telegramUserId }
      });

      console.log('📡 API: Onboarding data loaded', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to load onboarding data');
    } catch (error: any) {
      console.error('❌ Load onboarding data failed:', error);
      // Return empty data on error to not block onboarding
      return { has_data: false };
    }
  }

    async analyzeOnboardingSite(url: string, telegramUserId?: number): Promise<{ url: string; analysis: SiteAnalysisResult }> {
    const requestData: { url: string; telegram_user_id?: number } = { url };
    if (telegramUserId) {
      requestData.telegram_user_id = telegramUserId;
    }
    
    const response: AxiosResponse<ApiResponse<{ url: string; analysis: SiteAnalysisResult }>> = await this.api.post(
      '/onboarding/analyze-site',
      requestData
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Onboarding site analysis failed');
  }

  async createCampaignFromOnboarding(campaignData: {
    name: string;
    goal: 'sales' | 'leads' | 'awareness';
    budget: number;
    geo: string; // Now supports any region slug
    business_profile: SiteAnalysisResult;
    site_url: string;
    telegram_user_id: number; // Теперь обязательный параметр
  }): Promise<{ campaign_id: number; campaign: any }> {
    try {
      const response: AxiosResponse<ApiResponse<{ campaign_id: number; campaign: any }>> = await this.api.post(
        '/campaigns/create-from-onboarding',
        campaignData
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to create campaign';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Campaign creation failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }


  async saveOrganizationData(organizationData: {
    telegram_user_id: number;
    organization_name: string;
    organization_type: 'individual' | 'legal' | 'entrepreneur';
    tin: string;
    representative_last_name: string;
    representative_first_name: string;
    representative_middle_name?: string;
    organization_email: string;
    registration_country?: string;
    registration_region?: string;
    currency?: string;
    notification_language?: string;
  }): Promise<{ 
    user_id: number; 
    organization_saved: boolean; 
    yandex_direct_created: boolean; 
    yandex_direct_error?: string;
  }> {
    try {
      const response: AxiosResponse<ApiResponse<{ 
        user_id: number; 
        organization_saved: boolean; 
        yandex_direct_created: boolean; 
        yandex_direct_error?: string;
      }>> = await this.api.post(
        '/onboarding/save-organization-data',
        organizationData
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to save organization data';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Organization data save failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  async completeOnboarding(data: {
    telegram_user_id: number;
    campaign_id: number;
    organization_data: {
      organization_name: string;
      organization_type: 'individual' | 'legal' | 'entrepreneur';
      tin: string;
      representative_last_name: string;
      representative_first_name: string;
      representative_middle_name?: string;
      organization_email: string;
      registration_country?: string;
      registration_region?: string;
    };
  }): Promise<{
    message: string;
    data: {
        vk_ads: {
          agency_token_check: any;
          client_creation: any;
          client_token_creation: any;
          campaign_creation: any;
          ad_group_creation: any;
          banners_creation: any;
          existing_client_used: boolean;
          errors: string[];
        };
      yandex_direct: {
        campaign_creation: any;
        ad_group_creation: any;
        keywords_creation: any;
        ads_creation: any;
        ads_moderation: any;
        existing_client_used: boolean;
        errors: string[];
      };
      global_errors: string[];
    };
  }> {
    try {
      // init_data добавляется автоматически через interceptor в заголовок X-Telegram-Init-Data
      const requestData = { ...data };
      
      const response: AxiosResponse<ApiResponse<{
        message: string;
        data: {
        vk_ads: {
          agency_token_check: any;
          client_creation: any;
          client_token_creation: any;
          campaign_creation: any;
          ad_group_creation: any;
          banners_creation: any;
          existing_client_used: boolean;
          errors: string[];
        };
          yandex_direct: {
            campaign_creation: any;
            ad_group_creation: any;
            keywords_creation: any;
            ads_creation: any;
            ads_moderation: any;
            existing_client_used: boolean;
            errors: string[];
          };
          global_errors: string[];
        };
      }>> = await this.api.post(
        '/onboarding/complete',
        requestData,
        {
          timeout: 600000, // 10 минут для завершения онбординга
        }
      );

      console.log('📡 API: completeOnboarding response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to complete onboarding';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Onboarding completion failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  async createYandexDirectCampaign(campaignData: {
    campaigns: any[];
    telegram_user_id: number;
  }): Promise<any> {
    try {
      const response: AxiosResponse<any> = await this.api.post(
        '/campaigns/create-yandex-direct',
        campaignData
      );

      // Возвращаем полный ответ (включая сырой ответ от Яндекс.Директ)
      return response.data;

    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Yandex Direct campaign creation failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Отправка кампании на модерацию в Яндекс.Директ (без создания клиента)
  async sendCampaignToYandexModeration(campaignId: number, telegramUserId: number): Promise<{
    message: string;
    data: {
      yandex_direct: {
        campaign_creation: any;
        ad_group_creation: any;
        keywords_creation: any;
        ads_creation: any;
        ads_moderation: any;
        existing_client_used: boolean;
        errors: string[];
      };
      global_errors: string[];
    };
  }> {
    try {
      console.log('📡 API: Starting sendCampaignToYandexModeration request', { campaignId, telegramUserId });
      
      const response: AxiosResponse<ApiResponse<{
        message: string;
        data: {
          yandex_direct: {
            campaign_creation: any;
            ad_group_creation: any;
            keywords_creation: any;
            ads_creation: any;
            ads_moderation: any;
            existing_client_used: boolean;
            errors: string[];
          };
          global_errors: string[];
        };
      }>> = await this.api.post(
        '/campaigns/send-to-yandex-moderation',
        {
          campaign_id: campaignId,
          telegram_user_id: telegramUserId
        },
        {
          timeout: 600000, // 10 минут для отправки на модерацию
        }
      );

      console.log('📡 API: sendCampaignToYandexModeration response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to send campaign to Yandex moderation';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Send to Yandex moderation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Creative endpoints
  async generateOnboardingCreatives(data: {
    telegram_user_id: number;
    url: string;
    goal?: string;
    audience?: string;
    geo?: string;
  }): Promise<{
    campaign_id: number;
    creatives: {
      keywords?: string[];
      negatives?: string[];
      ads?: any[];
      graphic?: any;
      images?: Array<{
        id: number;
        url: string;
        size: string;
        concept_index: number;
        concept_name: string;
        prompt: string;
      }>;
    };
    errors: Record<string, string>;
    site_analysis?: {
      summary?: string;
      brand?: string;
      site_text?: string;
    };
  }> {
    try {
      console.log('📡 API: Starting generateOnboardingCreatives request', data);
      
      const response: AxiosResponse<ApiResponse<{
        campaign_id: number;
        creatives: {
          keywords?: string[];
          negatives?: string[];
          ads?: any[];
          graphic?: any;
          images?: Array<{
            id: number;
            url: string;
            size: string;
            concept_index: number;
            concept_name: string;
            prompt: string;
          }>;
        };
        errors: Record<string, string>;
        site_analysis?: {
          summary?: string;
          brand?: string;
          site_text?: string;
        };
      }>> = await this.api.post(
        '/onboarding/generate-creatives',
        data,
        {
          timeout: 600000, // 10 минут для создания объявлений
        }
      );

      console.log('📡 API: generateOnboardingCreatives response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to generate creatives';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Creative generation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Generate only text creatives for onboarding (step 6)
  async generateOnboardingTextCreatives(data: {
    telegram_user_id: number;
    url: string;
    goal?: string;
    audience?: string;
    geo?: string;
    campaign_id?: number;
  }): Promise<{
    campaign_id: number;
    creatives: {
      keywords?: string[];
      negatives?: string[];
      ads?: any[];
    };
    errors: Record<string, string>;
    site_analysis?: {
      summary?: string;
      brand?: string;
      site_text?: string;
    };
  }> {
    try {
      console.log('📡 API: Starting generateOnboardingTextCreatives request', data);
      
      const response: AxiosResponse<ApiResponse<{
        campaign_id: number;
        creatives: {
          keywords?: string[];
          negatives?: string[];
          ads?: any[];
        };
        errors: Record<string, string>;
        site_analysis?: {
          summary?: string;
          brand?: string;
          site_text?: string;
        };
      }>> = await this.api.post(
        '/onboarding/generate-text-creatives',
        data,
        {
          timeout: 600000, // 10 минут для текстовых объявлений
        }
      );

      console.log('📡 API: generateOnboardingTextCreatives response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to generate text creatives';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Text creative generation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Generate only graphic creatives for onboarding (step 7)
  async generateOnboardingGraphicCreatives(data: {
    telegram_user_id: number;
    campaign_id: number;
  }): Promise<{
    campaign_id: number;
    creatives: {
      graphic?: any;
      images?: Array<{
        id: number;
        url: string;
        size: string;
        concept_index: number;
        concept_name: string;
        prompt: string;
      }>;
    };
    errors: Record<string, string>;
  }> {
    try {
      console.log('📡 API: Starting generateOnboardingGraphicCreatives request', data);
      
      const response: AxiosResponse<ApiResponse<{
        campaign_id: number;
        creatives: {
          graphic?: any;
          images?: Array<{
            id: number;
            url: string;
            size: string;
            concept_index: number;
            concept_name: string;
            prompt: string;
          }>;
        };
        errors: Record<string, string>;
      }>> = await this.api.post(
        '/onboarding/generate-graphic-creatives',
        data,
        {
          timeout: 900000, // 15 минут для баннеров
        }
      );

      console.log('📡 API: generateOnboardingGraphicCreatives response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Если есть детали валидации, включаем их в ошибку
      const errorMessage = response.data.error || 'Failed to generate graphic creatives';
      if (response.data.details) {
        const validationErrors = Object.entries(response.data.details)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`${errorMessage} (${validationErrors})`);
      }

      throw new Error(errorMessage);
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Graphic creative generation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Generate additional banner for existing campaign
  async generateAdditionalBanner(data: {
    telegram_user_id: number;
    campaign_id: number;
    concept_name: string;
    prompt: string;
  }): Promise<{
    creative: any;
    banner: any;
  }> {
    try {
      console.log('📡 API: Starting generateAdditionalBanner request', data);
      
      const response: AxiosResponse<ApiResponse<{
        creative: any;
        banner: any;
      }>> = await this.api.post(
        '/onboarding/generate-additional-banner',
        data,
        {
          timeout: 300000, // 5 минут для генерации изображения
        }
      );

      console.log('📡 API: generateAdditionalBanner response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        const errorMessage = response.data.error || 'Failed to generate additional banner';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Additional banner generation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        }
      });
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Generate single image using creatives API endpoint
  async generateImage(prompt: string, size: string = '1024x1024', responseFormat: string = 'url'): Promise<{ url: string }> {
    try {
      console.log('📡 API: Starting generateImage request', { prompt: prompt.substring(0, 100) + '...', size, responseFormat });
      
      const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post(
        '/creatives/generate-image',
        {
          prompt,
          size,
          response_format: responseFormat
        },
        {
          timeout: 300000, // 5 минут для генерации изображения
        }
      );

      console.log('📡 API: generateImage response received', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to generate image');
    } catch (error: any) {
      console.error('❌ Image generation failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response?.status === 401) {
        throw new Error('Необходима авторизация для генерации изображений');
      }
      
      throw new Error(error.response?.data?.error || error.message || 'Не удалось сгенерировать изображение');
    }
  }

  async getUserCreatives(params: {
    telegram_user_id: number;
    source_url?: string;
    type?: string;
  }): Promise<{
    creatives: any[];
    count: number;
  }> {
    try {
      const response: AxiosResponse<ApiResponse<{
        creatives: any[];
        count: number;
      }>> = await this.api.get('/creatives', { params });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to get creatives');
    } catch (error: any) {
      // Логируем ошибку для отладки
      console.error('❌ Get creatives failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  async getCampaignCreatives(campaignId: number, telegramUserId: number): Promise<{
    creatives: any[];
  }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data: {
          creatives: any[];
        };
        error?: string;
      }> = await this.api.get('/creatives', { 
        params: { 
          telegram_user_id: telegramUserId,
          campaign_id: campaignId 
        } 
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to get campaign creatives');
    } catch (error: any) {
      console.error('❌ Get campaign creatives failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Campaign endpoints
  async getCampaigns(telegramUserId?: number): Promise<Campaign[]> {
    try {
      const params = telegramUserId ? { telegram_user_id: telegramUserId } : {};
      console.log('📡 Fetching campaigns with params:', params);
      
      const response: AxiosResponse<ApiResponse<Campaign[]>> = await this.api.get('/campaigns', { params });
      
      console.log('📥 Campaigns response:', {
        success: response.data.success,
        dataCount: response.data.data?.length || 0,
        status: response.status
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to fetch campaigns');
    } catch (error: any) {
      console.error('❌ getCampaigns error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  }

  async getCampaign(campaignId: number): Promise<Campaign> {
    const response: AxiosResponse<ApiResponse<Campaign>> = await this.api.get(`/campaigns/${campaignId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to fetch campaign');
  }

  // Statistics endpoints
  async getYandexStatistics(campaignId: number, dateFrom: string, dateTo: string): Promise<{
    success: boolean;
    data?: Array<{
      date: string;
      campaign_id: number;
      campaign_name?: string;
      cost: number;
      clicks: number;
      impressions: number;
    }>;
    error?: string;
    message?: string;
  }> {
    try {
      const response = await this.api.get(`/campaigns/${campaignId}/yandex-statistics`, {
        params: { date_from: dateFrom, date_to: dateTo }
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Yandex statistics failed:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to get Yandex statistics');
    }
  }

  async getVkStatistics(campaignId: number, dateFrom: string, dateTo: string): Promise<{
    success: boolean;
    data?: Array<{
      date: string;
      campaign_id: number;
      spent: number;
      impressions: number;
      clicks: number;
      ctr: number;
      cpc: number;
      cpm: number;
    }>;
    error?: string;
    message?: string;
  }> {
    try {
      const response = await this.api.get(`/campaigns/${campaignId}/vk-statistics`, {
        params: { date_from: dateFrom, date_to: dateTo }
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ VK statistics failed:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to get VK statistics');
    }
  }

  // async createCampaign(data: CreateCampaignData): Promise<Campaign> {
  //   const response = await this.api.post('/campaigns', data);
  //   return response.data.data;
  // }

  // Banner endpoints
  async getUserBanners(telegramUserId: number, campaignId?: number): Promise<{
    banners: Array<{
      id: number;
      title: string;
      description: string;
      image_url: string;
      image_size: string;
      concept_name: string;
      prompt: string;
      usage_count: number;
      created_at: string;
      campaign?: {
        id: number;
        name: string;
      };
    }>;
  }> {
    const params: { telegram_user_id: number; campaign_id?: number } = { 
      telegram_user_id: telegramUserId 
    };
    
    if (campaignId) {
      params.campaign_id = campaignId;
    }

    const response: AxiosResponse<ApiResponse<{
      banners: Array<{
        id: number;
        title: string;
        description: string;
        image_url: string;
        image_size: string;
        concept_name: string;
        prompt: string;
        usage_count: number;
        created_at: string;
        campaign?: {
          id: number;
          name: string;
        };
      }>;
    }>> = await this.api.get('/banners', { 
      params,
      timeout: 600000 // 10 минут для загрузки баннеров
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to get user banners');
  }

  // Campaign endpoints
  async getCampaignBanners(campaignId: number): Promise<{
    banners: Array<{
      id: number;
      title: string;
      description: string;
      image_url: string;
      image_size: string;
      concept_name: string;
      prompt: string;
      usage_count: number;
      created_at: string;
      creative?: {
        id: number;
        type: string;
        header: string;
      };
    }>;
  }> {
    const response: AxiosResponse<ApiResponse<{
      banners: Array<{
        id: number;
        title: string;
        description: string;
        image_url: string;
        image_size: string;
        concept_name: string;
        prompt: string;
        usage_count: number;
        created_at: string;
        creative?: {
          id: number;
          type: string;
          header: string;
        };
      }>;
    }>> = await this.api.get(`/campaigns/${campaignId}/banners`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to get campaign banners');
  }

  async getCampaignAds(campaignId: number): Promise<{
    ads: Array<{
      id: number;
      title: string;
      text: string;
      href: string;
      display_url_path: string;
      status: string;
      status_name: string;
      type?: string;
      type_name?: string;
      yandex_ad_id?: string;
      vk_ad_id?: string;
      yandex_data?: any;
      vk_data?: any;
      created_at: string;
      creative?: {
        id: number;
        type: string;
        header: string;
      };
    }>;
    yandex_image_ads: Array<{
      id: number;
      title: string;
      text: string;
      href: string;
      display_url_path: string;
      status: string;
      status_name: string;
      type?: string;
      type_name?: string;
      yandex_ad_id?: string;
      yandex_data?: any;
      created_at: string;
      creative?: {
        id: number;
        type: string;
        header: string;
      };
    }>;
    yandex_text_ads: Array<{
      id: number;
      title: string;
      text: string;
      href: string;
      display_url_path: string;
      status: string;
      status_name: string;
      type?: string;
      type_name?: string;
      yandex_ad_id?: string;
      yandex_data?: any;
      created_at: string;
      creative?: {
        id: number;
        type: string;
        header: string;
      };
    }>;
    has_yandex_image_ads: boolean;
    has_yandex_text_ads: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      ads: Array<{
        id: number;
        title: string;
        text: string;
        href: string;
        display_url_path: string;
        status: string;
        status_name: string;
        type?: string;
        type_name?: string;
        yandex_ad_id?: string;
        vk_ad_id?: string;
        yandex_data?: any;
        vk_data?: any;
        created_at: string;
        creative?: {
          id: number;
          type: string;
          header: string;
        };
      }>;
      yandex_image_ads: Array<{
        id: number;
        title: string;
        text: string;
        href: string;
        display_url_path: string;
        status: string;
        status_name: string;
        type?: string;
        type_name?: string;
        yandex_ad_id?: string;
        yandex_data?: any;
        created_at: string;
        creative?: {
          id: number;
          type: string;
          header: string;
        };
      }>;
      yandex_text_ads: Array<{
        id: number;
        title: string;
        text: string;
        href: string;
        display_url_path: string;
        status: string;
        status_name: string;
        type?: string;
        type_name?: string;
        yandex_ad_id?: string;
        yandex_data?: any;
        created_at: string;
        creative?: {
          id: number;
          type: string;
          header: string;
        };
      }>;
      has_yandex_image_ads: boolean;
      has_yandex_text_ads: boolean;
    }>> = await this.api.get(`/campaigns/${campaignId}/ads`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Failed to get campaign ads');
  }


  // Отправка кампании на модерацию в Яндекс.Директ
  async sendBannersToYandexModeration(campaignId: number, telegramUserId: number): Promise<{
    success: boolean;
    message: string;
    yandex_campaign_id?: number;
    yandex_ad_group_id?: number;
    ads_created?: number;
    images_uploaded?: number;
    ads_moderated?: number;
    errors?: string[];
  }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        message: string;
        yandex_campaign_id?: number;
        yandex_ad_group_id?: number;
        ads_created?: number;
        images_uploaded?: number;
        ads_moderated?: number;
        errors?: string[];
        error?: string;
      }> = await this.api.post(`/campaigns/${campaignId}/send-banners-to-yandex-moderation`, {
        telegram_user_id: telegramUserId
      });

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.error || 'Failed to send banners to Yandex moderation');
    } catch (error: any) {
      console.error('❌ Send banners to Yandex moderation failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw new Error(error.response?.data?.error || error.message || 'Не удалось отправить баннеры на модерацию');
    }
  }

  async sendTextAdsToYandexModeration(campaignId: number, telegramUserId: number): Promise<{
    success: boolean;
    message: string;
    yandex_campaign_id?: number;
    yandex_ad_group_id?: number;
    ads_created?: number;
    ads_moderated?: number;
    errors?: string[];
  }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        message: string;
        yandex_campaign_id?: number;
        yandex_ad_group_id?: number;
        ads_created?: number;
        ads_moderated?: number;
        errors?: string[];
        error?: string;
      }> = await this.api.post(`/campaigns/${campaignId}/send-text-ads-to-yandex-moderation`, {
        telegram_user_id: telegramUserId
      });

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.error || 'Failed to send text ads to Yandex moderation');
    } catch (error: any) {
      console.error('❌ Send text ads to Yandex moderation failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw new Error(error.response?.data?.error || error.message || 'Не удалось отправить текстовые объявления на модерацию');
    }
  }


  async getLatestCampaign(telegramUserId: number): Promise<{
    id: number;
    name: string;
    goal: string;
    budget: number;
    geo: string;
    status: string;
    site_url: string;
    created_at: string;
    updated_at: string;
  } | null> {
    const response: AxiosResponse<ApiResponse<{
      id: number;
      name: string;
      goal: string;
      budget: number;
      geo: string;
      status: string;
      site_url: string;
      created_at: string;
      updated_at: string;
    } | null>> = await this.api.get('/campaigns/latest', {
      params: { telegram_user_id: telegramUserId }
    });

    if (response.data.success) {
      return response.data.data || null;
    }

    throw new Error(response.data.error || 'Failed to get latest campaign');
  }

  // Delete keyword
  async deleteKeyword(data: {
    telegram_user_id: number;
    campaign_id: number;
    keyword: string;
  }): Promise<{
    message: string;
    active_keywords: string[];
  }> {
    try {
      console.log('🔍 Deleting keyword with data:', data);
      
      const response: AxiosResponse<ApiResponse<{
        message: string;
        active_keywords: string[];
      }>> = await this.api.delete('/creatives/keyword', { data });

      console.log('📥 Delete keyword response:', response.data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to delete keyword');
    } catch (error: any) {
      console.error('❌ Delete keyword failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
        console.error('📋 Response status:', error.response.status);
        console.error('📋 Response headers:', error.response.headers);
      }
      if (error.request) {
        console.error('📋 Request details:', {
          url: error.request.responseURL,
          method: error.request.method,
          headers: error.request.getAllResponseHeaders?.()
        });
      }
      throw new Error(error.response?.data?.error || error.message || 'Failed to delete keyword');
    }
  }

  // Delete negative keyword
  async deleteNegativeKeyword(data: {
    telegram_user_id: number;
    campaign_id: number;
    negative: string;
  }): Promise<{
    message: string;
    active_negatives: string[];
  }> {
    try {
      const response: AxiosResponse<ApiResponse<{
        message: string;
        active_negatives: string[];
      }>> = await this.api.delete('/creatives/negative-keyword', { data });

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to delete negative keyword');
    } catch (error: any) {
      console.error('❌ Delete negative keyword failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Regions
  async getRegions(): Promise<RegionsData> {
    const response: AxiosResponse<RegionsData> = await this.api.get('/regions');
    return response.data;
  }

  // Yandex Direct Balance (через AccountManagement API v4)
  async getYandexDirectAccountBalance(telegramUserId: number): Promise<{
    balance: number | null;
    balance_available: number | null;
    formatted_balance: string | null;
    formatted_balance_available: string | null;
    total_formatted: string | null;
    currency: string;
    account_id: string | null;
    has_balance: boolean;
    method: string;
  } | null> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data?: {
          balance: number | null;
          balance_available: number | null;
          total_balance: number | null;
          currency: string;
          balance_formatted: string | null;
          balance_available_formatted: string | null;
          total_balance_formatted: string | null;
          client_login?: string;
          account_id?: string | null;
          message?: string;
          method?: string;
        };
        error?: string;
      }> = await this.api.get('/yandex-direct/account-balance', {
        params: { telegram_user_id: telegramUserId }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        // Маппинг полей бэкенда на поля фронтенда
        return {
          balance: data.balance,
          balance_available: data.balance_available,
          formatted_balance: data.balance_formatted,
          formatted_balance_available: data.balance_available_formatted,
          total_formatted: data.total_balance_formatted,
          currency: data.currency || 'RUB',
          account_id: data.account_id || null,
          has_balance: data.balance !== null && data.balance > 0,
          method: data.method || 'AccountManagement'
        };
      }

      // Если баланс не найден - возвращаем null без ошибки
      return null;
    } catch (error: any) {
      // Тихо логируем ошибку, не выбрасываем исключение
      console.log('📋 Yandex Direct account balance not available:', error.response?.data?.error || error.message);
      return null;
    }
  }

  // Support Tickets
  async submitSupportTicket(data: { message: string; telegram_user_id: number; }): Promise<{
    success: boolean;
    message: string;
    ticket_id: number;
  }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        message: string;
        ticket_id: number;
      }> = await this.api.post('/tickets', data);

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.message || 'Failed to submit support ticket');
    } catch (error: any) {
      console.error('❌ Submit support ticket failed:', error);
      if (error.response?.data) {
        console.error('📋 Server response:', error.response.data);
      }
      throw new Error(error.response?.data?.message || error.message || 'Не удалось создать обращение');
    }
  }

  // Utility methods
  isAuthenticated(): boolean {
    // С init_data авторизацией - проверяем наличие init_data provider
    if (this.initDataProvider) {
      const initData = this.initDataProvider();
      return !!(initData && initData.trim());
    }
    return false;
  }

  hasInitData(): boolean {
    if (this.initDataProvider) {
      const initData = this.initDataProvider();
      return !!(initData && initData.trim());
    }
    return false;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 